<template>
	<div class="flex w-full flex-col gap-0.5">
		<label for="address-input" class="text-sm leading-none"> Adres </label>
		<AppPopoverSelect
			v-model="selectedAddressId"
			v-model:search-value="search"
			input-id="address-input"
			:options="addressOptions"
			:placeholder="addressPlaceholder"
			:empty-state-label="emptyStateLabel"
			ignore-filter
			input-class="h-7 rounded-[3px]"
			popover-class="w-[300px]"
			@close="closeWithoutCommit"
		/>
	</div>
</template>

<script setup lang="ts">
import type { AddressResponse } from "@loanlink-nl/model-app";
import { watchDebounced } from "@vueuse/core";
import { computed, ref, watch } from "vue";
import type { Ref } from "vue";
import AppPopoverSelect from "@/components/input/AppPopoverSelect.vue";
import type { Address } from "@/generatedApi";
import { addressToLabel } from "@/helpers/uiHelpers";
import { api } from "@/loanLinkApi";

const minimalSearchLength = 3;

const address = defineModel<AddressResponse | undefined>();

const search = ref("");
const addresses = useAddressSearch(search);
const selectedAddressId = computed({
	get: () =>
		(address.value as Address | undefined)?.nummeraanduidingIdentificatie ??
		address.value?.adresseerbaarObjectIdentificatie,
	set(value) {
		if (!value) {
			address.value = undefined;
			return;
		}

		const selectedAddress = addresses.value.find(
			(foundAddress) => foundAddress.nummeraanduidingIdentificatie === value,
		);
		if (selectedAddress) {
			address.value = selectedAddress;
		}
	},
});
const addressOptions = computed(() =>
	addresses.value.flatMap((foundAddress) =>
		foundAddress.nummeraanduidingIdentificatie
			? [
					{
						value: foundAddress.nummeraanduidingIdentificatie,
						label: addressToLabel(foundAddress),
					},
				]
			: [],
	),
);

const emptyStateLabel = computed(() =>
	search.value.length >= minimalSearchLength
		? "Geen adressen gevonden"
		: "Zoek een adres",
);
const addressPlaceholder = computed(() =>
	address.value ? "" : "Zoek een adres",
);

watch(
	address,
	() => {
		closeWithoutCommit();
		blurActiveElement();
	},
	{ immediate: true },
);

function closeWithoutCommit() {
	syncSearchWithAddress();
}

function syncSearchWithAddress() {
	search.value = addressToLabel(address.value);
}

function blurActiveElement() {
	if (document.activeElement instanceof HTMLElement) {
		document.activeElement.blur();
	}
}

function useAddressSearch(search: Ref<string>) {
	const addresses = ref<Address[]>([]);
	let requestId = 0;

	watchDebounced(
		search,
		async (query) => {
			if (query.length < minimalSearchLength) {
				addresses.value = [];
				return;
			}

			const currentRequestId = ++requestId;

			try {
				const { data } = await api.address.getAddress({
					q: query,
				});

				if (currentRequestId !== requestId || search.value !== query) {
					return;
				}

				addresses.value = data?.result ?? [];
			} catch (err) {
				if (err instanceof Error && err.name === "AbortError") {
					return;
				}

				throw err;
			}
		},
		{
			debounce: 200,
			maxWait: 200,
		},
	);

	return addresses;
}
</script>
